import { ref } from 'vue'
import { supabase } from '../lib/supabase'

/**
 * Cached own-row users record (role + profile_completed) plus capability flags.
 * A portal user can be both opdrachtgever and installateur — capabilities are
 * derived from the existence of the opdrachtgevers/installateurs row, not from
 * the single users.role enum.
 *
 * Lives outside the Pinia auth store so the router guard can use it during the
 * initial navigation (before any component — and thus the store's onMounted
 * initialization — has run). The auth store re-exports it for components.
 */
export interface ProfileMeta {
  role: string
  profile_completed: boolean
  /** True when an opdrachtgevers row exists for this user */
  isOpdrachtgever: boolean
  /** True when an installateurs row exists for this user */
  isInstallateur: boolean
}

export const profileMeta = ref<ProfileMeta | null>(null)

let cachedUserId: string | null = null
let inflight: Promise<ProfileMeta | null> | null = null

export function resetProfileMeta() {
  profileMeta.value = null
  cachedUserId = null
  inflight = null
}

export function markProfileCompleted() {
  if (profileMeta.value) {
    profileMeta.value = { ...profileMeta.value, profile_completed: true }
  }
}

export async function ensureProfileMeta(): Promise<ProfileMeta | null> {
  const {
    data: { session },
  } = await supabase.auth.getSession()
  const userId = session?.user?.id
  if (!userId) {
    resetProfileMeta()
    return null
  }

  if (cachedUserId === userId && profileMeta.value) {
    return profileMeta.value
  }
  if (cachedUserId === userId && inflight) {
    return inflight
  }

  cachedUserId = userId
  inflight = (async () => {
    const [userResult, opdrachtgeverResult, installateurResult] = await Promise.all([
      supabase.from('users').select('role, profile_completed').eq('id', userId).maybeSingle(),
      supabase.from('opdrachtgevers').select('id').eq('id', userId).maybeSingle(),
      supabase.from('installateurs').select('id').eq('id', userId).maybeSingle(),
    ])

    const metaError = userResult.error || opdrachtgeverResult.error || installateurResult.error
    if (metaError) {
      throw metaError
    }

    profileMeta.value = userResult.data
      ? {
          role: userResult.data.role,
          profile_completed: userResult.data.profile_completed,
          isOpdrachtgever: !!opdrachtgeverResult.data || userResult.data.role === 'opdrachtgever',
          isInstallateur: !!installateurResult.data || userResult.data.role === 'installateur',
        }
      : null
    return profileMeta.value
  })()

  try {
    return await inflight
  } catch (error) {
    // Don't cache failures — the next call should retry.
    resetProfileMeta()
    throw error
  } finally {
    inflight = null
  }
}
